import { createContext, useCallback, useEffect, useState } from 'react';
import { localStorageKeys } from '../config/localStorageKeys';
import { ITenant } from '@clickersphere/core/entities/ITenant';
import { ITenantMembership } from '@clickersphere/core/entities/ITenantMembership';
import { IUser } from '@clickersphere/core/entities/IUser';
import { RoleEnum } from '@clickersphere/core/enums/RoleEnum';
import { useSignOut } from '../hooks/login/useSignOut';
import { useTenantSignIn } from '../hooks/tenant/useTenantSignIn';
import useLoader from '../hooks/useLoader';
import { useUserMe } from '../hooks/user/useUserMe';

interface IAuthContextValue {
  isAuthenticated: boolean | undefined;
  isAuthChecking: boolean;
  isAuthLoading: boolean;
  loggedInUser: IUser | undefined;
  loggedInUserRole: RoleEnum | null;
  tenant: ITenant;
  signIn: () => void;
  signOut: () => Promise<void>;
  tenantSignIn: (tenant: ITenant) => Promise<void>;
}

export const AuthContext = createContext<IAuthContextValue>(
  {} as IAuthContextValue,
);

export function AuthContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    () =>
      localStorage.getItem(localStorageKeys.HAS_SESSION) === 'true'
        ? undefined
        : false,
  );
  const [tenantMemberships, setTenantMemberships] = useState<
    ITenantMembership[]
  >([]);
  const [tenant, setTenant] = useState<ITenant>({} as ITenant);
  const { tenantSignInAsync, tenantSignInIsPending } = useTenantSignIn();
  const { signOutAsync } = useSignOut();
  const { handleLoaderStateChange } = useLoader();
  const {
    loggedInUser,
    isLoggedInUserLoading,
    isLoggedInUserError,
    isLoggedInUserSuccess,
  } = useUserMe(isAuthenticated);

  const signIn = useCallback(() => {
    setIsAuthenticated(true);
    localStorage.setItem(localStorageKeys.HAS_SESSION, 'true');
  }, []);

  const signOut = useCallback(async () => {
    setIsAuthenticated(false);
    localStorage.removeItem(localStorageKeys.HAS_SESSION);
    handleLoaderStateChange(false);
    try {
      await signOutAsync();
    } catch {}
  }, [signOutAsync, handleLoaderStateChange]);

  const tenantSignIn = useCallback(
    async (value: ITenant) => {
      if (tenant.id === value.id) return;
      setTenant(value);
      await tenantSignInAsync(value.id);
    },
    [tenantSignInAsync, tenant.id],
  );

  useEffect(() => {
    if (isLoggedInUserSuccess) {
      setIsAuthenticated(true);
      localStorage.setItem(localStorageKeys.HAS_SESSION, 'true');
    } else if (isLoggedInUserError) {
      signOut();
    }
  }, [isLoggedInUserSuccess, isLoggedInUserError, signOut]);

  useEffect(() => {
    if (loggedInUser) {
      setTenantMemberships(loggedInUser.memberships);

      if (loggedInUser.selectedTenantId) {
        setTenant(
          loggedInUser.memberships.find(
            (m) => m.tenant.id === loggedInUser.selectedTenantId,
          )?.tenant || loggedInUser.memberships[0]?.tenant,
        );
      }
    }
  }, [loggedInUser]);

  useEffect(() => {
    window.addEventListener('logoutEvent', signOut);

    return () => {
      window.removeEventListener('logoutEvent', signOut);
    };
  }, [signOut]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthChecking: isAuthenticated === undefined,
        isAuthLoading: isLoggedInUserLoading || tenantSignInIsPending,
        loggedInUser,
        tenant,
        loggedInUserRole:
          (tenantMemberships.find((m) => m.tenant.id === tenant.id)
            ?.role as RoleEnum) || null,
        signIn,
        signOut,
        tenantSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
